import * as React from 'react';

import { cn } from '@/lib/cn';
import { Card } from './card';
import { Skeleton, SkeletonCard } from './skeleton';
import { useSkeletonClock } from './skeleton-clock';

/**
 * Which page the skeleton stands in for. Each variant keeps the geometry of
 * its real page (header, cards, spacing), so the swap does not jump.
 *
 * - `form`: Configuración. Header, section index, stacked form cards.
 * - `account`: Mi cuenta. Header, tab bar, a card, the support card.
 * - `stats`: Estadísticas. Header, filter row, figures, chart and side card.
 * - `tour`: a tour's page. Header with actions, tab bar, panorama grid.
 * - `plan`: Plan y uso. Header, the ink hero with its figures, the plans.
 * - `table`: Miembros. Header, a table of rows.
 * - `gallery`: the org / collection home. Header, search row, tour cards
 *   (grid at the chosen size, or rows: see `PageSkeletonProps.view`).
 */
export type PageSkeletonVariant = 'form' | 'account' | 'stats' | 'tour' | 'plan' | 'table' | 'gallery';

/** How the tours of the `gallery` variant are laid out: the user's stored choice, so the swap does not jump. */
export interface PageSkeletonView {
  mode: 'gallery' | 'list';
  size: 'small' | 'medium' | 'large';
}

const DEFAULT_VIEW: PageSkeletonView = { mode: 'gallery', size: 'medium' };

/**
 * Columns of the tour gallery at each size; the real gallery uses these same
 * classes. Literal strings, so Tailwind picks them up. `medium` is the default.
 */
export const GALLERY_GRID_CLASS: Record<PageSkeletonView['size'], string> = {
  small: 'grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5',
  medium: 'grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3',
  large: 'grid grid-cols-1 gap-5 lg:grid-cols-2',
};

export interface PageSkeletonProps {
  variant: PageSkeletonVariant;
  /** `gallery` only: grid size or list, as the member has it stored. Defaults to the medium grid. */
  view?: PageSkeletonView;
  /** Accessible name of the loading group (one announcement for the whole page). */
  label?: string;
  className?: string;
}

/** Title (30px type) and, optionally, the line under it, as `PageHeader` lays them out. */
function HeaderSkeleton({
  subtitle = true,
  actions,
  className,
}: {
  subtitle?: boolean;
  actions?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn('mb-[22px] flex flex-wrap justify-between gap-x-4 gap-y-3', subtitle ? 'items-end' : 'items-center', className)}
    >
      <div className="min-w-0">
        <div className="flex h-[33px] items-center">
          <Skeleton className="h-7 w-52" />
        </div>
        {subtitle ? (
          <div className="mt-1.5 flex h-5 items-center">
            <Skeleton className="h-3.5 w-72 max-w-full" />
          </div>
        ) : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-1.5">{actions}</div> : null}
    </div>
  );
}

/** A form field: label line over a 36px input. */
function FieldSkeleton({ label = 'w-20' }: { label?: string }) {
  return (
    <div className="space-y-2">
      <Skeleton className={cn('h-3.5', label)} />
      <Skeleton className="h-9 w-full" />
    </div>
  );
}

/** A form card: title, one line of help and some fields. */
function FormCardSkeleton({ fields = 2, className }: { fields?: number; className?: string }) {
  return (
    <div className={cn('rounded-card border border-line bg-surface p-5 shadow-e1 sm:p-6', className)}>
      <div className="space-y-2.5">
        <Skeleton className="h-5 w-28" />
        <Skeleton className="h-3.5 w-3/5" />
      </div>
      <div className="mt-6 space-y-5">
        {Array.from({ length: fields }).map((_, i) => (
          <FieldSkeleton key={i} label={i % 2 === 0 ? 'w-20' : 'w-28'} />
        ))}
      </div>
    </div>
  );
}

function TabBarSkeleton({ tabs }: { tabs: number }) {
  return (
    <div className="flex h-10 items-center gap-2 border-b border-line">
      {Array.from({ length: tabs }).map((_, i) => (
        <Skeleton key={i} className="h-8 w-24" />
      ))}
    </div>
  );
}

function FormVariant() {
  return (
    <div>
      <HeaderSkeleton />
      <div className="flex items-start gap-9">
        {/* The section index only exists from `lg`, like the real one. */}
        <div className="hidden w-52 shrink-0 flex-col gap-0.5 self-start lg:flex">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="flex h-9 items-center px-2.5">
              <Skeleton className={cn('h-3.5', i % 2 === 0 ? 'w-20' : 'w-28')} />
            </div>
          ))}
        </div>
        <div className="w-full min-w-0 max-w-[760px] space-y-3.5">
          <FormCardSkeleton fields={2} />
          <FormCardSkeleton fields={3} />
          <FormCardSkeleton fields={2} />
        </div>
      </div>
    </div>
  );
}

function AccountVariant() {
  return (
    <div className="mx-auto w-full max-w-4xl">
      <HeaderSkeleton />
      <TabBarSkeleton tabs={3} />
      <div className="pt-5">
        <FormCardSkeleton fields={3} />
      </div>
      <Card className="mt-8 space-y-3 p-5">
        <Skeleton className="h-5 w-36" />
        <Skeleton className="h-3.5 w-2/5" />
        <Skeleton className="h-8 w-56" />
      </Card>
    </div>
  );
}

function StatsVariant() {
  return (
    <div className="mx-auto max-w-[1220px]">
      <HeaderSkeleton />
      <div className="mb-[22px] flex flex-wrap items-center gap-2.5">
        <Skeleton className="h-9 w-full sm:w-52" />
        <Skeleton className="h-9 w-full sm:w-52" />
        <Skeleton className="h-9 w-full sm:w-64" />
      </div>
      <div className="space-y-4">
        <div className="grid grid-cols-2 items-stretch gap-4 lg:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Card key={i} className="min-h-[144px] space-y-3 p-4">
              <Skeleton className="h-3.5 w-20" />
              <Skeleton className="h-8 w-24" />
              <Skeleton className="h-3 w-16" />
            </Card>
          ))}
        </div>
        <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-12">
          <Card className="space-y-4 p-5 lg:col-span-8">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-[250px] w-full" />
          </Card>
          <Card className="space-y-4 p-5 lg:col-span-4">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-[250px] w-full" />
          </Card>
        </div>
      </div>
    </div>
  );
}

function TourVariant() {
  return (
    <div>
      <HeaderSkeleton
        subtitle={false}
        actions={
          <>
            <Skeleton className="size-9" />
            <Skeleton className="h-9 w-36" />
            <Skeleton className="h-9 w-40" />
          </>
        }
      />
      <div className="space-y-4">
        <TabBarSkeleton tabs={7} />
        <div className="space-y-3">
          <div className="flex h-11 items-center justify-between gap-3">
            <Skeleton className="h-3.5 w-28" />
            <div className="hidden items-center gap-2 sm:flex">
              <Skeleton className="h-9 w-20" />
              <Skeleton className="h-9 w-32" />
              <Skeleton className="h-9 w-32" />
            </div>
          </div>
          {/* The panorama list, the tab a tour opens on. */}
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <Card key={i} className="flex items-center gap-3 p-2 shadow-none">
                <Skeleton className="h-12 w-[72px] shrink-0" />
                <div className="min-w-0 flex-1 space-y-2">
                  <Skeleton className={cn('h-3.5', i % 2 === 0 ? 'w-40' : 'w-28')} />
                  <Skeleton className="h-3 w-10" />
                </div>
                <Skeleton className="hidden h-8 w-20 sm:block" />
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function PlanVariant() {
  return (
    <div className="mx-auto max-w-[1180px]">
      <HeaderSkeleton />
      <Card material="ink" className="px-7 pb-6 pt-[26px]">
        <Skeleton className="h-3.5 w-24" />
        <Skeleton className="mt-3 h-12 w-48" />
        <Skeleton className="mt-3 h-3.5 w-80 max-w-full" />
        <div className="mt-[26px] grid grid-cols-2 gap-3 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-3.5">
              <Skeleton className="size-[34px] shrink-0" />
              <div className="space-y-2">
                <Skeleton className="h-3 w-16" />
                <Skeleton className="h-6 w-10" />
              </div>
            </div>
          ))}
        </div>
      </Card>
      <div className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i} className="space-y-4 p-6">
            <Skeleton className="h-5 w-20" />
            <Skeleton className="h-9 w-28" />
            <Skeleton className="h-3.5 w-full" />
            <Skeleton className="h-3.5 w-4/5" />
            <Skeleton className="h-9 w-full" />
          </Card>
        ))}
      </div>
    </div>
  );
}

function TableVariant() {
  return (
    <div className="space-y-5">
      <HeaderSkeleton actions={<Skeleton className="h-9 w-28" />} />
      <Card className="divide-y divide-line overflow-hidden p-0">
        <div className="flex h-9 items-center px-4">
          <Skeleton className="h-2.5 w-16" />
        </div>
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 px-4 py-3">
            <Skeleton shape="pill" className="size-9 shrink-0" />
            <div className="min-w-0 flex-1 space-y-1.5">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-44 max-w-full" />
            </div>
            <Skeleton className="hidden h-8 w-28 sm:block" />
          </div>
        ))}
      </Card>
    </div>
  );
}

/** The tours as rows, with the geometry of the real list rows. */
function ListRowsSkeleton() {
  return (
    <div aria-hidden="true" className="space-y-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <Card key={i} className="flex items-center gap-4 p-2">
          <Skeleton className="h-12 w-19 shrink-0 lg:h-auto lg:w-24 lg:aspect-[16/10]" />
          <Skeleton className="h-3.5 w-1/3" />
        </Card>
      ))}
    </div>
  );
}

function GalleryVariant({ view = DEFAULT_VIEW }: { view?: PageSkeletonView }) {
  return (
    <div className="space-y-6">
      {/* Wrapped so `mb-0` doesn't cancel the row gap, as in `TourHome`. Below `lg`
          "Nuevo tour" is a floating button and the filters are one icon button. */}
      <div>
        <HeaderSkeleton className="mb-0" actions={<Skeleton className="hidden h-9 w-32 lg:block" />} />
      </div>
      <div className="flex items-center gap-2 lg:justify-between lg:gap-x-4">
        <Skeleton className="h-9 min-w-0 flex-1 lg:max-w-sm" />
        <Skeleton className="size-9 shrink-0 lg:hidden" />
        <div className="hidden items-center gap-2 lg:flex">
          <Skeleton className="h-9 w-64" />
          <Skeleton className="h-9 w-52" />
          <Skeleton className="h-9 w-20" />
        </div>
      </div>
      {view.mode === 'list' ? (
        <ListRowsSkeleton />
      ) : (
        <div className={GALLERY_GRID_CLASS[view.size]}>
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonCard key={i} announce={false} compact={view.size === 'small'} />
          ))}
        </div>
      )}
    </div>
  );
}

const VARIANTS: Record<PageSkeletonVariant, (props: { view?: PageSkeletonView }) => React.ReactElement> = {
  form: FormVariant,
  account: AccountVariant,
  stats: StatsVariant,
  tour: TourVariant,
  plan: PlanVariant,
  table: TableVariant,
  gallery: GalleryVariant,
};

/**
 * A page while its first content loads, with the real structure of what is
 * coming. One `role="status"` for the whole page: the blocks inside are
 * hidden from assistive tech. It fades in after ~100 ms (kit-next.css), so a
 * page that loads at once never shows it, and the page's entrance (`PageRise`)
 * skips it. Use it for the first load of a page only: a refresh keeps the old
 * content on screen, and a modal keeps a spinner.
 *
 * It is the ONE placeholder of its page: a page that loads its data renders
 * this and nothing else (no header plus inline blocks), so the handover from
 * the route's fallback to the page's own skeleton to the content is a single
 * swap. Skeletons share a clock (`useSkeletonClock`), so one replacing another
 * continues the same fade and shimmer instead of restarting them. The `gallery`
 * variant takes the member's stored `view` so its grid matches the real one.
 */
export function PageSkeleton({ variant, view, label = 'Cargando', className }: PageSkeletonProps) {
  const Variant = VARIANTS[variant];
  const ref = useSkeletonClock<HTMLDivElement>();
  return (
    <div ref={ref} data-slot="page-skeleton" data-variant={variant} role="status" aria-label={label} className={className}>
      <Variant view={view} />
    </div>
  );
}
